
import { ConsoleLogger } from "src/util/logger";
import { ObjectValues } from "src/util/utilTypes";

// Events that our app want to listen
// List of all the trulience events - https://trulience.com/docs#/client-sdk/sdk?id=trulience-events
export const AvatarEvent = {
  AuthSuccess: "auth-success",
  MicUpdate: "mic-update",
  MicAccess: "mic-access",
  SpeakerUpdate: "speaker-update",
  TrlChat: "trl-chat",
  WebsocketConnect: "websocket-connect",
  WebsocketClose: "websocket-close",
  WebsocketMessage: "websocket-message",
  MediaConnected: "media-connected",
  ModalDialogOpen: "modal-dialog-open",
  ModalDialogClose:"modal-dialog-close",
  AvatarLoadProgress: "load-progress"
} as const

// Events that our app post
const AvatarPostEvent = {
  TrulienceMethodCall: "trl-method-call"
} as const

const TrulienceMethod = {
  sendMessage: "sendMessage",
  sendMessageToAvatar: "sendMessageToAvatar",
  setMicEnabled: "setMicEnabled",
  setSpeakerEnabled: "setSpeakerEnabled",
  toggleMic: "toggleMic",
  toggleSpeaker: "toggleSpeaker",
  fixAudioContext: "fixAudioContext"
}

export const AvatarMessageType = {
  ChatText: 3
} as const

type AvatarEventType = ObjectValues<typeof AvatarEvent>
type AvatarPostEventType = ObjectValues<typeof AvatarPostEvent>
type TrulienceMethodType = ObjectValues<typeof TrulienceMethod>
type EventCallback = (data: any) => void;

const logger = new ConsoleLogger()

class AvatarCommunication {
  private static instance: AvatarCommunication | null = null;
  private iframeElement: HTMLIFrameElement | null = null;
  private eventListeners: Record<AvatarEventType, EventCallback[]> = {} as Record<AvatarEventType, EventCallback[]>;
  private handleMessageBound: (event: MessageEvent<any>) => void;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): AvatarCommunication {
    if (!AvatarCommunication.instance) {
      AvatarCommunication.instance = new AvatarCommunication();
    }
    return AvatarCommunication.instance;
  }

  // Initializer method
  public init(iframeElement: HTMLIFrameElement): void {
    if (!this.iframeElement) {
      this.iframeElement = iframeElement;
      this.handleMessageBound = this.handleMessage.bind(this);
      window.addEventListener('message', this.handleMessageBound);
    }
  }

  // Handle incoming messages from the iframe
  private handleMessage(event: MessageEvent): void {
    const { eventName, eventParams } = event.data;
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName].forEach(callback => callback(eventParams));
    }
  }


  // Subscribe to an event
  public on(event: AvatarEventType, callback: EventCallback): void {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  // Unsubscribe from an event
  public off(event: AvatarEventType, callback: EventCallback): void {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    }
  }

  // Method to post messages to the iframe
  public postMessage(eventName: AvatarPostEventType, eventParams: any): void {
    if (this.iframeElement) {
      this.iframeElement.contentWindow?.postMessage({ eventName, eventParams }, '*');
    } else {
      logger.warn('AvatarConnector: iframeElement is not initialized.');
    }
  }

  public executeTrulienceMethod(method: TrulienceMethodType, args?: any[], callId?: string): void {
    this.postMessage(AvatarPostEvent.TrulienceMethodCall , { method, args, callId });
  }


  // Predefined methods to interact with the iframe
  public setMicStatus(status: boolean): void {
    this.executeTrulienceMethod(TrulienceMethod.setMicEnabled, [status])
  }

  public setSpeakerStatus(status: boolean): void {
    this.executeTrulienceMethod(TrulienceMethod.setSpeakerEnabled, [status])
  }

  public toggleMic(): void {
    this.executeTrulienceMethod(TrulienceMethod.toggleMic)
  }

  public toggleSpeaker(): void {
    this.executeTrulienceMethod(TrulienceMethod.toggleSpeaker)
  }

  public fixAudioContext(): void {
    this.executeTrulienceMethod(TrulienceMethod.fixAudioContext)
  }

  public sendMessage(message: string): void {
    this.executeTrulienceMethod(TrulienceMethod.sendMessage, [message])
  }

  public sendMessageToAvatar(message: string): void {
    this.executeTrulienceMethod(TrulienceMethod.sendMessageToAvatar, [message])
  }

  public enableJoystick(): void {
    this.sendMessageToAvatar("<trl-enable-joystick />")
  }

  public disableJoystick(): void {
    this.sendMessageToAvatar("<trl-disable-joystick />")
  }

  // Reset method to clear the instance and listeners
  public reset(): void {
    this.iframeElement = null;
    this.eventListeners = {} as Record<AvatarEventType, EventCallback[]>;
    window.removeEventListener('message', this.handleMessageBound);
    AvatarCommunication.instance = null;
  }
}

// Export the singleton instance
export const avatarCommunication = AvatarCommunication.getInstance();
